import { Undef } from 'types/UtilityTypes';
import captureException from 'utils/captureException';

import { FormStorageServiceInterface, SavedFormState } from './types';

/* Full key format: formState_{tmsName}_{threadId} */
const MAX_FORM_STATES = 20;

function enforceLRULimit(): void {
  try {
    const entries: Array<{ key: string; lastAccessedAt: number }> = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('formState_')) {
        try {
          const storedState = localStorage.getItem(key);
          if (storedState) {
            const parsed = JSON.parse(storedState) as SavedFormState<
              unknown,
              unknown
            >;
            entries.push({ key, lastAccessedAt: parsed.lastAccessedAt || 0 });
          }
        } catch {
          continue;
        }
      }
    }

    if (entries.length > MAX_FORM_STATES) {
      entries.sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      const toDelete = entries.length - MAX_FORM_STATES;
      for (let i = 0; i < toDelete; i++) {
        localStorage.removeItem(entries[i].key);
      }
    }
  } catch (error) {
    captureException(error, {
      functionName: 'FormStorageService.enforceLRULimit',
    });
  }
}

// TODO: Save clicked suggestion too
export const FormStorageService: FormStorageServiceInterface = {
  storageKey: 'formState',

  saveFormState<T, U>(key: string, formState: SavedFormState<T, U>): void {
    try {
      enforceLRULimit();
      const stateToSave = JSON.stringify({
        ...formState,
        lastAccessedAt: Date.now(),
      });
      localStorage.setItem(`${this.storageKey}_${key}`, stateToSave);
    } catch (error) {
      captureException(error, {
        functionName: 'FormStorageService.saveFormState',
      });
    }
  },

  getFormState<T, U>(key: string): Undef<SavedFormState<T, U>> {
    try {
      const storedState = localStorage.getItem(`${this.storageKey}_${key}`);
      if (storedState) {
        const parsed = JSON.parse(storedState) as SavedFormState<T, U>;
        const updated = { ...parsed, lastAccessedAt: Date.now() };
        try {
          localStorage.setItem(
            `${this.storageKey}_${key}`,
            JSON.stringify(updated)
          );
          return updated;
        } catch {
          // If update fails, return original state
          return parsed;
        }
      }
    } catch (error) {
      captureException(error, {
        functionName: 'FormStorageService.getFormState',
      });
    }
    return undefined;
  },

  clearFormState(key: string): void {
    try {
      localStorage.removeItem(`${this.storageKey}_${key}`);
    } catch (error) {
      captureException(error, {
        functionName: 'FormStorageService.clearFormState',
      });
    }
  },
};
